import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useNavigate, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import debounce from 'lodash/debounce';
import ScheduledPayoutComponent from "./component";
import * as XLSX from 'xlsx';

const ScheduledPayoutContainer = (props) => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [apiResponse, setApiResponse] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [chargeSummery, setChargeSummery] = useState(null);
  const [currentPayoutId, setCurrentPayoutId] = useState(null);
  const [payoutReportData, setPayoutReportData] = useState([]);

  // Cohort type mapping - ONLY 2, 4, and 13
  const cohortTypeMapping = {
    "2": "Franchise",
    "4": "City",
    "13": "PDF",
  };

  const getCohortTypeDisplayName = (typeId) => {
    if (!typeId) return "N/A";
    const typeIdStr = typeId.toString();
    return cohortTypeMapping[typeIdStr] || typeIdStr;
  };

  useEffect(() => {
    fetchScheduledPayoutData();
  }, [date]);

  useEffect(() => {
    const amount = bills
      .filter(bill => selectedBills.includes(bill.payoutId))
      .reduce((sum, bill) => sum + bill.netPayable, 0);
    setSelectedAmount(amount);
  }, [selectedBills, bills]);

  const fetchScheduledPayoutData = debounce(() => {
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v3/acc/payouts/pending?date=${date}`,
      'GET',
      undefined,
      (response) => {
        if (response) {
          setBills(response);
          calculateTotal(response);
          if (response.length > 0) {
            setCurrentPayoutId(response[0].payoutId);
          }
        } else {
          setBills([]);
          setTotalAmount(0);
        }
        setLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error(err.message || "Error", "Failed to load scheduled payouts");
        setLoading(false);
      }
    );
  }, 300);

  const fetchChargeBillSummery = (payoutId) => {
    if (!payoutId) {
      toastr.warning("Warning", "No payout ID available");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v3/acc/payout/${payoutId}/deduction`,
      'GET',
      undefined,
      (response) => {
        setChargeSummery(response);
        setLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error(err.message || "Error", "Failed to load charge summary");
        setLoading(false);
      }
    );
  };

  const calculateTotal = (bills) => {
    const total = bills.reduce((sum, bill) => sum + bill.netPayable, 0);
    setTotalAmount(total);
  };

  const handleCheckboxChange = (billId) => {
    setSelectedBills(prev => {
      if (prev.includes(billId)) {
        return prev.filter(id => id !== billId);
      } else {
        return [...prev, billId];
      }
    });
  };

  const handleSelectAll = (e, filteredBills) => {
    if (e.target.checked) {
      setSelectedBills(filteredBills.map(bill => bill.payoutId));
    } else {
      setSelectedBills([]);
    }
  };

  const formatDateForExcel = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const handleDownloadExcel = (reportData) => {
    try {
      // Handle response structure
      const responseData = reportData?.cohortScheduleResponseDto || reportData;
      
      if (!responseData || responseData.length === 0) {
        toastr.warning("Warning", "No data available for export");
        return;
      }

      // Create a map of payoutId to original bill data
      const billsMap = {};
      bills.forEach(bill => {
        billsMap[bill.payoutId] = bill;
      });

      // Remove duplicates
      const uniqueData = [];
      const seenPayoutIds = new Set();
      
      responseData.forEach(item => {
        if (!seenPayoutIds.has(item.payoutId)) {
          seenPayoutIds.add(item.payoutId);
          
          const originalBill = billsMap[item.payoutId];
          
          // Calculate deductions
          let totalDeductions = 0;
          if (Array.isArray(item.deductions)) {
            totalDeductions = item.deductions.reduce((sum, d) => sum + (d.amount || 0), 0);
          } else {
            totalDeductions = item.deductions || 0;
          }
          
          // Get cohort type ID from item or originalBill
          const cohortTypeId = item.cohortTypeId || originalBill?.cohortTypeId;
          
          // Get display name - ONLY for 2, 4, 13
          let cohortTypeDisplay = "N/A";
          if (cohortTypeId) {
            const typeIdStr = cohortTypeId.toString();
            cohortTypeDisplay = cohortTypeMapping[typeIdStr] || typeIdStr;
          }
          
          uniqueData.push({
            "Partner ID": item.franchiseeId || originalBill?.franchiseeId || "N/A",
            "Partner Name": item.payee || originalBill?.name || "N/A",
            "Cohort ID": item.cohortId || originalBill?.cohortId || "N/A",
            "Cohort Type": cohortTypeDisplay,
            "Cohort Sales End Date": originalBill?.cohortSalesEnd 
              ? formatDateForExcel(originalBill.cohortSalesEnd)
              : "N/A",
            "Status": item.status || "Generated",
            "Gross Sales": item.grossSale || 0,
            "Deductions": totalDeductions,
            "Payout": item.payout || 0,
            "SAAS": item.saas || 0,
            "RAAS": item.raas || 0,
            "PG Fee": item.pgFee || 0,
            "Rental": item.rental || 0,
            "Misc Credit": item.miscCredit || 0,
            "Misc Debit": item.miscDebit || 0,
            "Transfer Text": item.transferText || "N/A",
          });
        }
      });

      const ws = XLSX.utils.json_to_sheet(uniqueData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Payouts Report");
      
      const formattedDate = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `payout-report-${formattedDate}.xlsx`);

      toastr.success("Success", "Excel file downloaded successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toastr.error("Error", "Failed to download Excel file");
    }
  };

  const handleMarkSettled = () => {
    if (selectedBills.length === 0) {
      toastr.warning("Warning", "Please select at least one bill to mark as settled");
      return;
    }

    setLoading(true);
    
    props.getDataFromAPI(
      '/partner/api/v3/acc/payouts/mark-schedule/report',
      "PATCH",
      { payoutIds: selectedBills },
      (response) => {
        console.log("API Success:", response);
        
        setApiResponse(response);
        setPayoutReportData(response || []);
        setShowSuccessDialog(true);
        setShowResponseDialog(true);
        
        handleDownloadExcel(response);
        
        setSelectedBills([]);
        setLoading(false);
        toastr.success("Success", "Payouts marked as scheduled successfully");
      },
      (err) => {
        console.error("API Error:", err);
        
        if (err?.response?.data?.success === true) {
          console.log("API was successful despite error callback");
          setLoading(false);
          return;
        }
        
        setLoading(false);
        toastr.error(err?.message || "Failed to mark bills as settled");
      }
    );
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    navigate(`/home/configuration/payoutpartners/schedule/download-report`, {
      state: { payoutReportData }
    });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <ScheduledPayoutComponent
      loading={loading}
      date={date}
      bills={bills}
      selectedBills={selectedBills}
      totalAmount={selectedBills.length === bills.length ? totalAmount : selectedAmount}
      onCheckboxChange={handleCheckboxChange}
      onSelectAll={handleSelectAll}
      onMarkSettled={handleMarkSettled}
      onGoBack={handleGoBack}
      apiResponse={apiResponse}
      showSuccessDialog={showSuccessDialog}
      onSuccessClose={handleSuccessClose}
      showResponseDialog={showResponseDialog}
      setShowResponseDialog={setShowResponseDialog}
      fetchChargeBillSummery={fetchChargeBillSummery}
      chargeSummery={chargeSummery}
      currentPayoutId={currentPayoutId}
      setCurrentPayoutId={setCurrentPayoutId}
      cohortTypeMapping={cohortTypeMapping}
      getCohortTypeDisplayName={getCohortTypeDisplayName}
    />
  );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {
  getDataFromAPI,
})(ScheduledPayoutContainer);